/**
 * The customer receipt - the 80mm thermal layout, redesigned against a real
 * FBR-integrated receipt (Jamil Sweets, Islamabad) the owner supplied as the
 * shape to match:
 *
 *     logo
 *     address / phone / NTN
 *     -----------------------------------------------
 *     POS ID: X          Invoice#: <authority number>
 *     Sr. No. | Date | Time | Cashier
 *     -----------------------------------------------
 *     Sr  Description   Price  Qty  Amount
 *     -----------------------------------------------
 *     Payment Mode          Cash / Card
 *     Item: n     Total Amount
 *                 Sales Tax 16%
 *                 POS Service Fee
 *                 Total
 *     -----------------------------------------------
 *     FISCAL INVOICE NO. <authority number>
 *     [FBR/PRA logo]                            [QR]
 *
 * A few things about this file are deliberate and worth not "tidying" away:
 *
 *   Tables, not flexbox. Thermal print drivers are the least capable renderers
 *   this app will ever meet. A table with fixed column widths comes out right
 *   on every printer; a flex row silently collapses on some of them.
 *
 *   A monospace face and tabular figures. The amount column has to line up,
 *   which a proportional font makes "999,999.00" and "1.00" fail to share a
 *   right edge on.
 *
 *   Everything comes from the stored order via printService. Nothing is
 *   recomputed here, so a receipt reprinted next year shows what was charged
 *   then, not what today's prices would make of it.
 *
 *   No round-off line. The total already reflects it; the reference receipt
 *   this was matched against does not itemise it either, and a few paisa of
 *   rounding is not worth a line a customer has to interpret.
 *
 * The fiscal block at the foot is what makes this a legal invoice: the
 * authority's own invoice number and the QR that encodes it. Before the
 * gateway has answered, it prints a clear "awaiting" line instead of a
 * number - a receipt that shows a blank where the invoice number should be is
 * worse than one that says why.
 */

import { money, formatDate, formatTime } from '../../utils/format';
import fbrLogo from '../../assets/FBR_Logo.png';
/* .jpg, not .png: the file is a JPEG - PRA's own "POS COMPONENT" mark - that
   arrived with a .png name on it. The browser's image decoder refuses to
   render a file whose real bytes do not match the type the server declares
   from that wrong extension, which is why this logo intermittently failed to
   load: it depended on whether a stale cached copy from before the mismatch
   happened to still be in the browser's cache. */
import praLogo from '../../assets/PRA_Logo.jpeg';
import { assetUrl } from '../../services/api';

const AUTHORITY_LOGOS = { FBR: fbrLogo, PRA: praLogo };

const Line = ({ solid = false }) => <div className={solid ? 'rule-solid' : 'rule'} />;

/**
 * A long, unbroken authority invoice number - "817408FI3O52312525*test*" -
 * has no spaces for a browser to wrap on, so left alone it overflows its box
 * rather than wrapping, and on paper that means printing past the right edge
 * exactly like the width bug this receipt was already fixed for once. This
 * class lets the browser break it anywhere it needs to instead.
 */
const LONG_VALUE_CLASS = 'break-all';

/** label on the left, value hard right - the totals block. */
const Row = ({ label, value, bold = false, indent = false }) => (
    <tr className={bold ? 'font-bold' : ''}>
        <td className={indent ? 'pl-3' : ''}>{label}</td>
        <td className="num">{value}</td>
    </tr>
);

const Receipt = ({ receipt, isPreview = false }) => {
    if (!receipt) return null;

    const { template, business, invoice, items, totals, taxBreakup, payments, qrCode } = receipt;
    const widthClass = template.paperWidthMM === 58 ? 'receipt-58' : 'receipt-80';
    // A till whose printer clips at the class default overrides it here rather
    // than through a code change - see the comment on .receipt-80 in index.css.
    const widthOverride = template.contentWidthMM ? `${template.contentWidthMM}mm` : undefined;
    const currency = totals.currency || 'Rs.';

    // A settled bill has a fiscal number, or a reason it does not yet.
    const hasFiscalNumber = !!invoice.fiscalInvoiceNumber;
    const fiscalPending = template.showFiscalNumber && !hasFiscalNumber
        && invoice.fiscalStatus !== 'NOT_REQUIRED';

    /**
     * The part of PRA's invoice number after its 6-digit POS ID prefix.
     *
     * PRA's own number always starts with the POS ID it was issued under -
     * "817408FI3O52312525" for POS ID 817408 - which is a fixed part of PRA's
     * own numbering, not something that depends on what this till's Setup
     * screen currently has configured. That matters on a reprint: if the POS
     * ID has since been changed, the number on an old invoice still starts
     * with the ID it was actually filed under, not today's - so this slices a
     * fixed six characters off a PRA number rather than checking it against
     * whatever posId happens to be configured right now. The POS ID has its
     * own line at the top of the receipt already, so repeating it as the
     * first six characters of the number below is not new information, just
     * six digits eating into an already narrow column. FBR's own numbers do
     * not carry this prefix, so only PRA's are ever shortened this way.
     */
    const fiscalNumberTail = hasFiscalNumber && invoice.authorityShortName === 'PRA'
        ? invoice.fiscalInvoiceNumber.slice(6)
        : invoice.fiscalInvoiceNumber;

    // The authority's own invoice number already starts with the POS ID it was
    // issued under (PRA's IMS returns e.g. "817408FI3O52149210" for POS ID
    // 817408) - so the two are shown side by side here rather than one built
    // by concatenating them, which would double the ID up on the paper.
    const hasLogo = template.showLogo && business.logoPath;

    return (
        <div
            /* Flush against the printable area's own left edge, not centred
               within it. Centring assumes the blank space either side of a
               deliberately-narrowed receipt (see .receipt-80 in index.css) is
               even, and on real hardware it is not always - the paper's own
               left margin plus our centring math stacked into an "unusual"
               left-side gap that was never intended, on top of whatever the
               printer's own edge already costs. Flush-left leaves that one
               real margin as the only one. */
            className={`receipt ${widthClass} ${isPreview ? 'mx-auto border border-dashed border-slate-300 p-3' : 'p-1'}`}
            style={{ fontSize: `${template.fontSizePx || 11}px`, ...(widthOverride ? { width: widthOverride } : null) }}
        >
            {/* ---------------------------------------------------- header */}
            {hasLogo ? (
                <img
                    src={assetUrl(business.logoPath)}
                    alt={business.name || ''}
                    className="mx-auto block h-16 max-w-full object-contain print-force-colour"
                />
            ) : (
                <p className="text-center text-[1.25em] font-bold leading-tight">
                    {business.name}
                </p>
            )}

            {template.showAddress && business.addressLines?.map((line, i) => (
                <p key={i} className="text-center leading-tight">{line}</p>
            ))}

            {(business.phone || business.mobile) && (
                <p className="text-center leading-tight">
                    {business.phone && `Ph : ${business.phone}`}
                    {business.phone && business.mobile && '  '}
                    {business.mobile && `Mobile : ${business.mobile}`}
                </p>
            )}

            {template.showNtnStrn && (business.strn || business.ntn) && (
                <p className="text-center leading-tight">
                    {business.strn && `STRN : ${business.strn}`}
                    {business.strn && business.ntn && ',  '}
                    {business.ntn && `NTN : ${business.ntn}`}
                </p>
            )}

            {template.headerText && (
                <p className="mt-1 text-center leading-tight">{template.headerText}</p>
            )}

            <Line solid />

            {/* ------------------------------------------- invoice meta */}
            {/* One line: now that the invoice number prints with its POS ID
                prefix already stripped (fiscalNumberTail, above), it is short
                enough to share a row with "POS ID: ..." without either one
                getting squeezed into an unreadable wrap. */}
            {invoice.posId && (
                <div className="flex justify-between gap-1 leading-tight">
                    <span className="shrink-0">POS ID: {invoice.posId}</span>
                    {hasFiscalNumber && (
                        <span className={`text-right ${LONG_VALUE_CLASS}`}>Inv: {fiscalNumberTail}</span>
                    )}
                </div>
            )}

            <table className="mt-1">
                <thead>
                    {/* Bold, like the items table's own header below - a column
                        heading, so it follows the same convention rather than
                        the plain data row it sits above. */}
                    <tr>
                        <th className="w-[32%] pb-1 text-left font-bold">Sr. No.</th>
                        <th className="w-[28%] pb-1 text-left font-bold">Date</th>
                        <th className="w-[16%] pb-1 text-left font-bold">Time</th>
                        {template.showCashier && <th className="pb-1 text-right font-bold">Cashier</th>}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {/* Our own short invoice number, not the order's ticket
                            number - "ORD-20260903-000012" does not fit this
                            column, and is not what the till's own books key
                            on. */}
                        <td className="whitespace-nowrap">{invoice.invoiceNumber || invoice.orderNumber}</td>
                        <td className="whitespace-nowrap">{formatDate(invoice.date)}</td>
                        <td className="whitespace-nowrap">{formatTime(invoice.date)}</td>
                        {template.showCashier && (
                            // First name only - a full name is the thing most
                            // likely to wrap this row onto two lines at this
                            // width, and a cashier is identified on the floor
                            // by first name anyway.
                            <td className="text-right">{invoice.cashierName?.split(' ')[0] || '-'}</td>
                        )}
                    </tr>
                </tbody>
            </table>

            {/* Table / customer, only where they mean something */}
            {(template.showTableNo && invoice.tableNumber) || invoice.customerName ? (
                <table className="mt-1">
                    <tbody>
                        {template.showTableNo && invoice.tableNumber && (
                            <tr>
                                <td>Table</td>
                                <td className="num">
                                    {invoice.tableNumber}
                                    {invoice.guestCount > 1 && ` (${invoice.guestCount} guests)`}
                                </td>
                            </tr>
                        )}
                        {invoice.customerName && invoice.customerName !== 'Walk-in Customer' && (
                            <tr>
                                <td>Customer</td>
                                <td className="num">
                                    {invoice.customerName}
                                    {invoice.customerMobile && ` / ${invoice.customerMobile}`}
                                </td>
                            </tr>
                        )}
                        {invoice.customerNtn && (
                            <tr>
                                <td>Buyer NTN</td>
                                <td className="num">{invoice.customerNtn}</td>
                            </tr>
                        )}
                        {invoice.deliveryAddress && (
                            <tr>
                                <td colSpan={2} className="pt-0.5">
                                    Deliver to: {invoice.deliveryAddress}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            ) : null}

            <Line solid />

            {/* ------------------------------------------------- the items */}
            <table>
                <thead>
                    <tr>
                        <th className="w-[8%] pb-1 text-left">Sr</th>
                        <th className="pb-1 text-left">Description</th>
                        <th className="w-[20%] pb-1 text-right">Price</th>
                        <th className="w-[12%] pb-1 text-right">Qty</th>
                        <th className="w-[24%] pb-1 text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => (
                        <tr key={item.serial} className="align-top">
                            <td>{item.serial}</td>
                            <td className="pr-1">
                                {item.description}
                                {/* Modifiers and notes belong under the line, indented -
                                    they change what was served, so they belong on the
                                    customer's copy too. */}
                                {item.modifiers?.length > 0 && (
                                    <div className="pl-2 text-[0.9em]">
                                        {item.modifiers.map((modifier, i) => (
                                            <div key={i}>
                                                + {modifier.name}
                                                {modifier.price > 0 && ` (${money(modifier.price)})`}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {item.note && <div className="pl-2 text-[0.9em] italic">* {item.note}</div>}
                                {template.showItemDiscount && item.discount > 0 && (
                                    <div className="pl-2 text-[0.9em]">Discount: -{money(item.discount)}</div>
                                )}
                            </td>
                            <td className="num">{money(item.rate)}</td>
                            <td className="num">{item.quantity}</td>
                            <td className="num">{money(item.amount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Line solid />

            {/* ------------------------------------------------- the money */}
            {payments.length > 0 && (
                // Right after the label, not pushed to the far right edge like
                // the totals rows below - "Cash" is read as one phrase with
                // "Payment Mode", not looked up in a column.
                <p className="font-bold leading-tight">
                    Payment Mode: {payments.map((p) => p.mode).join(' / ')}
                </p>
            )}

            <table className="mt-1">
                <tbody>
                    <tr>
                        <td className="align-top" style={{ width: '28%' }}>
                            Item: {totals.itemCount}
                        </td>
                        <td>
                            <table>
                                <tbody>
                                    <Row label="Total Amount" value={money(totals.grossAmount)} />

                                    {totals.discount > 0 && (
                                        <Row label="Discount" value={`-${money(totals.discount)}`} />
                                    )}

                                    {totals.serviceCharge > 0 && (
                                        <Row
                                            label={`Service Charge ${totals.serviceChargePercent}%`}
                                            value={money(totals.serviceCharge)}
                                        />
                                    )}

                                    {totals.deliveryCharge > 0 && (
                                        <Row label="Delivery" value={money(totals.deliveryCharge)} />
                                    )}

                                    {/* One line per rate: a bill can mix 16% food and
                                        18% packaged goods, and both must be shown.

                                        With inclusive pricing the tax is already
                                        inside Total Amount, so the line is marked
                                        as such - otherwise the receipt would show
                                        three figures that do not sum to the
                                        fourth. */}
                                    {template.showTaxBreakup && taxBreakup.length > 0
                                        ? taxBreakup.map((tax, i) => (
                                            <Row
                                                key={i}
                                                label={`Sales Tax ${tax.label}${totals.pricesIncludeTax ? ' (incl.)' : ''}`}
                                                value={money(tax.taxAmount)}
                                            />
                                        ))
                                        : totals.taxAmount > 0 && (
                                            <Row
                                                label={`Sales Tax${totals.pricesIncludeTax ? ' (incl.)' : ''}`}
                                                value={money(totals.taxAmount)}
                                            />
                                        )}

                                    {totals.posServiceFee > 0 && (
                                        <Row label="POS Service Fee" value={money(totals.posServiceFee)} />
                                    )}

                                    {/* No Round Off line - the figure is folded
                                        into Total below rather than itemised. */}
                                    <Row label="Total" value={money(totals.totalPayable)} bold />
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Change is what the customer checks before walking away. */}
            {totals.changeAmount > 0 && (
                <table className="mt-1">
                    <tbody>
                        <Row label="Cash Received" value={money(totals.paidAmount)} />
                        <Row label="Change" value={money(totals.changeAmount)} bold />
                    </tbody>
                </table>
            )}

            <Line solid />

            {/* -------------------------------------------- fiscal block */}
            {template.showFiscalNumber && (
                <div className="text-center">
                    {hasFiscalNumber ? (
                        <p className={`font-bold leading-tight ${LONG_VALUE_CLASS}`}>
                            {invoice.authorityShortName || 'FBR'} INVOICE NO.
                            {' '}
                            {/* Full number here, unlike the shortened one next
                                to POS ID above - this is the one line on the
                                receipt that has to stand alone as the complete,
                                legally citable invoice number. */}
                            <span className={`text-[1.05em] ${LONG_VALUE_CLASS}`}>
                                {invoice.fiscalInvoiceNumber}
                            </span>
                        </p>
                    ) : fiscalPending ? (
                        // Honest rather than blank. The sale is recorded and will
                        // be reported; saying so is better than an empty line the
                        // customer or an inspector has to interpret.
                        <p className="leading-tight">
                            Invoice queued for {invoice.authorityShortName || 'the revenue authority'}
                            {invoice.fiscalStatus === 'OFFLINE' && ' (offline)'}
                        </p>
                    ) : null}

                    {/* A sandbox invoice must never be mistaken for a live one. */}
                    {invoice.isProduction === false && hasFiscalNumber && (
                        <p className="font-bold leading-tight">*** TEST INVOICE - NOT VALID ***</p>
                    )}
                </div>
            )}

            <div className="mt-2 flex items-end justify-between gap-2">
                {template.showAuthorityLogo && AUTHORITY_LOGOS[invoice.authorityShortName] && (
                    <img
                        src={AUTHORITY_LOGOS[invoice.authorityShortName]}
                        alt={invoice.authorityShortName}
                        /* PRA's real mark is landscape (1220x536, "PRA" +
                           icon + "POS COMPONENT"), not the square placeholder
                           this box was first sized for - this box's own ratio
                           (40:18) is close enough to the source's that
                           object-cover crops next to nothing, while still
                           filling the box exactly rather than leaving contain's
                           letterboxed gap on the sides. */
                        className="h-[18mm] w-[40mm] shrink-0 object-cover print-force-colour"
                    />
                )}

                {template.showQrCode && qrCode && (
                    <img
                        src={qrCode}
                        alt="Fiscal QR"
                        className="ml-auto h-[22mm] w-[22mm] shrink-0 print-force-colour"
                    />
                )}
            </div>

            {template.footerText && (
                <p className="mt-2 text-center leading-tight">{template.footerText}</p>
            )}

            {/* A reprint has to say so - otherwise two copies of one bill look
                like two sales. */}
            {invoice.reprintCount > 0 && (
                <p className="mt-1 text-center text-[0.85em]">
                    ** REPRINT #{invoice.reprintCount} **
                </p>
            )}

            {/* Whitespace so the tear-off does not cut the last line. */}
            <div className="h-6" />
        </div>
    );
};

export default Receipt;
